import { AxiosResponse } from "axios";
import { endPoints } from "utils/constants";
import { crudService } from "services/crud.service";
import { NotificationManager } from "react-notifications";
import {
  blockDeleteErrorModified,
  defaultMetaObj,
  deleteErrorResponse,
  initialProjectBlockState,
} from "store/constants";
import { getErrorMessage, getPaginatedUrlWithSearchParams } from "store/helper";
import { PayloadAction, createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const { projectBlockEndPoint } = endPoints;

export const fetchAllProjectBlocks = createAsyncThunk(
  "blocks/fetchAllProjectBlocks",
  async (
    {
      page,
      limit,
      searchParamsObj = {},
    }: { page: number; limit?: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      limit,
      endpoint: projectBlockEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteProjectBlock = createAsyncThunk(
  "blocks/deleteProjectBlock",
  async ({ blockId }: any, { rejectWithValue }) => {
    try {
      const response: any = await crudService.delete(
        projectBlockEndPoint,
        blockId
      );

      if (response.status === 204) {
        NotificationManager.success("Block Deleted Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(
        errorMessage?.includes(deleteErrorResponse)
          ? blockDeleteErrorModified
          : errorMessage
      );
      return rejectWithValue(error.message);
    }
  }
);

export const addNewProjectBlock = createAsyncThunk(
  "blocks/addNewProjectBlock",
  async (newBlock: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.create(
        `${projectBlockEndPoint}/`,
        newBlock
      );

      if (response.status === 201) {
        NotificationManager.success("Block Created Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const updateProjectBlock = createAsyncThunk(
  "blocks/updateProjectBlock",
  async ({ updatedBlock, blockId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        projectBlockEndPoint,
        blockId,
        updatedBlock
      );
      if (response.status === 204) {
        NotificationManager.success("Block data updated successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

const projectBlockSlice = createSlice({
  name: "blocks",
  initialState: initialProjectBlockState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch all blocks
    builder
      .addCase(fetchAllProjectBlocks.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllProjectBlocks.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.blocks = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllProjectBlocks.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.meta = defaultMetaObj;
        }
      );

    // delete Block
    builder
      .addCase(deleteProjectBlock.pending, (State) => {
        State.loading = true;
      })
      .addCase(
        deleteProjectBlock.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        deleteProjectBlock.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // add new block
    builder
      .addCase(addNewProjectBlock.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        addNewProjectBlock.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(addNewProjectBlock.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    //   update Block
    builder
      .addCase(updateProjectBlock.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateProjectBlock.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        updateProjectBlock.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );
  },
});

export const projectBlockReducer = projectBlockSlice.reducer;
